/**
 * Teslyn Cloud's Audit log page for a company (R166, decision 927; docs/specs/cloud/audit-log.md): the kinds of
 * change in words, who did it, and the dates searched. The rows are the company's `audit_event` rows, read through
 * `company_audit_log` (20261102150000_alert_devices_history_audit.sql) by its tenant admins and managers.
 *
 * What the log holds: changes (29 tables record theirs), Teslyn Support's access (grants, each page opened and each
 * change made) and client previews. Reads in general are not recorded.
 */

/** Who may open the page: as the `audit_select` policy (tenant admins and managers). */
export const AUDIT_LOG_ROLES: readonly string[] = ['tenant_admin', 'manager'];

export function canReadAuditLog(roles: readonly string[]): boolean {
  return roles.some((r) => AUDIT_LOG_ROLES.includes(r));
}

/** The kinds of change, by the first part of the recorded action ("project.status" → "project"). */
export const AUDIT_FAMILY_LABEL: Readonly<Record<string, string>> = {
  aircraft: 'Aircraft register',
  alert: 'Alerts',
  approval: 'Client approvals',
  archive: 'File archive',
  backup: 'Backups',
  clauses: 'Quote and contract clauses',
  client_membership: 'Client users',
  client_notice: 'Client notices',
  contract: 'Contracts',
  diagnostics: 'Diagnostics',
  document_scan: 'Document scans',
  export: 'Data exports',
  file: 'Project files',
  files: 'Project files',
  final_report_text: 'Final report text',
  flight: 'Flights',
  geosoft_template: 'Geosoft templates',
  hazard_pack: 'Hazard packs',
  hazard_pin: 'Hazard pins',
  height_breach: 'Height breach reports',
  integration: 'Integrations',
  invoice: 'Invoices',
  jsa: 'JSAs',
  jsp_meeting: 'JSP safety meetings',
  land_parcel: 'Land parcels',
  licence: 'Licence, seats and devices',
  lines_file: 'Lines files',
  membership: 'Staff sign-ins and roles',
  person: 'Staff register',
  photo: 'Photos',
  platform: 'Teslyn settings',
  portal: 'Client portal and Preview as client',
  portal_map: 'Client portal map',
  project: 'Projects',
  public_traffic: 'Public air traffic',
  qc: 'QC and line acceptance',
  quote: 'Quotes',
  refly_flag: 'Refly flags',
  refly_list: 'Refly list',
  remote_support: 'Remote support',
  report: 'Reports',
  safety_entry: 'Safety register',
  security: 'Two-step sign-in',
  service: 'Service switches',
  signoff: 'Safety sign-off',
  staff: 'Staff',
  station_health: 'Station health',
  support: 'Teslyn Support access',
  sync: 'Teslyn Field sync',
  tenant: 'Company settings',
};

/** A kind of change in words; one not in the table is its own name, readable ("land_parcel" → "Land parcel"). */
export function auditFamilyLabel(family: string): string {
  const known = AUDIT_FAMILY_LABEL[family];
  if (known) return known;
  const words = family.replace(/[_-]+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Other';
}

/** The "Kind of change" choices for the families found in the log, by their words, "Every kind" first. */
export function auditFamilyOptions(families: readonly string[]): readonly (readonly [string, string])[] {
  const seen = [...new Set(families.filter((f) => /^[a-z][a-z0-9_-]{0,60}$/.test(f)))];
  return [
    ['', 'Every kind'],
    ...seen.map((f) => [f, auditFamilyLabel(f)] as const).sort((a, b) => a[1].localeCompare(b[1], 'en-AU')),
  ];
}

/** Who a row names, as the database says (`company_audit_actor`). */
export interface AuditActor {
  readonly actor_name: string;
  readonly actor_email: string;
  /** 'person': a member of the company; 'system': Teslyn's own account; 'teslyn': Teslyn Support or Teslyn itself. */
  readonly actor_kind: 'person' | 'system' | 'teslyn' | string;
}

/** "Morgan Crew", "Teslyn Support (support@…)", "Teslyn" (a change Teslyn made by itself), "System (…)". */
export function auditWho(a: AuditActor): string {
  const name = a.actor_name.trim();
  const email = a.actor_email.trim();
  if (a.actor_kind === 'system') return email ? `System (${email})` : 'System';
  if (a.actor_kind === 'teslyn') return email ? `Teslyn Support (${email})` : 'Teslyn';
  return name || email || 'Someone';
}

/** The page's period: from the start of `from` to the end of `to`, each a yyyy-mm-dd date in the viewer's time. */
export interface AuditPeriod {
  readonly from: string | null;
  readonly to: string | null;
  readonly problem: string | null;
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The times `company_audit_log` takes for two dates typed on the page ("" for none). `offsetMin(date)` is the
 * viewer's offset from UTC on that date in minutes, east positive (Brisbane +600), so a day is the viewer's own day.
 * `to` is the start of the day after (not included). A "from" after "to" is a problem, said in words.
 */
export function auditPeriod(from: string, to: string, offsetMin: (date: string) => number): AuditPeriod {
  const start = (d: string): number | null => {
    const m = DAY.exec(d.trim());
    if (!m) return null;
    const ms = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!);
    return Number.isFinite(ms) ? ms - offsetMin(d.trim()) * 60_000 : null;
  };
  const f = from.trim() ? start(from) : null;
  const t0 = to.trim() ? start(to) : null;
  if ((from.trim() && f === null) || (to.trim() && t0 === null))
    return { from: null, to: null, problem: 'Enter dates as day, month and year.' };
  const t = t0 === null ? null : t0 + 86_400_000;
  if (f !== null && t !== null && f >= t)
    return { from: null, to: null, problem: 'The “From” date is after the “To” date.' };
  return {
    from: f === null ? null : new Date(f).toISOString(),
    to: t === null ? null : new Date(t).toISOString(),
    problem: null,
  };
}
